"use client";

import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { CustomScroll } from "@/components/CustomScroll";
import { RichText } from "@/components/RichText";
import { PageIndicators } from "@/components/ui/PageIndicators";
import { useIsMobile } from "@/features/layout/useIsMobile";
import { useProject, useProjectConfigs } from "@/features/project/ProjectContext";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useModal } from "@/hooks/useModal";
import { cn } from "@/lib/cn";
import { APP_INFO_MODAL_ID, appInfoImageUrl, pagesFor } from "../appInfo";
import { cmsUrl } from "../cmsUrl";

/**
 * The project's introduction: a few authored pages a visitor steps through
 * before reaching the map.
 *
 * Whether it opens is decided by `useOnboarding`, not here — the same first-visit
 * check also decides whether the tutorial shows instead, and the two can't both
 * take the screen.
 */
export function AppInfoModal() {
  const t = useTranslations("app-info");
  const locale = useLocale();
  const isMobile = useIsMobile();
  const configs = useProjectConfigs();
  // The project's own domain, not `configs.domain` — the sync doesn't put one
  // there, so every illustration resolved to nothing.
  const domain = useProject()?.domain;
  const { isOpen, close } = useModal(APP_INFO_MODAL_ID);

  const [page, setPage] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, isOpen, close);

  useEffect(() => {
    if (isOpen) ref.current?.focus();
  }, [isOpen]);

  const pages = pagesFor(configs.appInfoModal, locale);
  if (!isOpen || pages.length === 0) return null;

  const current = pages[Math.min(page, pages.length - 1)];
  const isLast = page >= pages.length - 1;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={current.title || t("label")}
      tabIndex={-1}
      // The tenant's colour at 75%, so the map reads as being behind it. Legacy
      // recomputed the rgba in JS on every render; the palette is already on the
      // document as CSS variables, and the utility resolves against them.
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-75"
    >
      <div
        className={cn(
          "flex w-full max-w-lg flex-col items-center justify-center bg-white p-9",
          isMobile && "mx-5"
        )}
      >
        <button
          type="button"
          onClick={close}
          aria-label={t("buttons.close")}
          className="mb-2 flex w-full justify-end"
        >
          <Image src="/icons/close.svg" alt="" width={12} height={12} />
        </button>

        <CustomScroll className="max-h-[calc(100vh-200px)]">
          <div className="flex flex-col items-center justify-center">
            <p className="-tracking-2 mb-11 text-xl font-semibold text-black">
              {current.title}
            </p>

            {(() => {
              const src = appInfoImageUrl(current.image, domain, cmsUrl());
              return src ? (
                <div className="p-3">
                  {/* Authored in the CMS at arbitrary dimensions, and served from
                      it — see `appInfoImageUrl`. */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={src} alt="" />
                </div>
              ) : null;
            })()}

            <div className="app-info-description mb-11 text-center text-xs md:text-sm">
              <RichText html={current.description ?? ""} />
            </div>
          </div>
        </CustomScroll>

        <div className="mt-3 flex items-center justify-center">
          <button
            type="button"
            onClick={close}
            className="rounded-full border border-[var(--primary)] bg-white px-5 py-2.5 text-base text-black hover:bg-[var(--primary)]"
          >
            {t("buttons.goToMap")}
          </button>

          {!isLast && (
            <button
              type="button"
              onClick={() => setPage(page + 1)}
              className="ms-5 rounded-full border border-[var(--primary)] bg-white px-5 py-2.5 text-base text-black hover:bg-[var(--primary)]"
            >
              {t("buttons.next")}
            </button>
          )}
        </div>

        <PageIndicators
          count={pages.length}
          activeIndex={page}
          onSelect={setPage}
          label={(index) => `${index + 1} / ${pages.length}`}
        />
      </div>
    </div>
  );
}
